/**
 * ProtocolDetail — the Manage-tab detail drawer (Sheet).
 *
 * Shows a protocol's version history with per-version lifecycle actions
 * (submit / archive / download spec) and the working version's documents
 * (its spec's primary source and related artifacts), with a "Suggest
 * documents" affordance (a document search built from the version itself)
 * that links a match into the draft's spec.
 *
 * The API says nothing up front about what the caller may change, so the
 * write controls show and a refusal answers 403, whose detail is shown.
 *
 * Clinical-document mutations (submit, archive, link a document) are
 * SERVER-AUTHORITATIVE per frontend/CLAUDE.md — no optimistic UI. Each calls
 * the API then invalidates the relevant query so the row reflects committed
 * state only.
 *
 * Four async states per section (versions, documents, suggestions): error+retry,
 * loading skeleton, empty, data.
 *
 * Design: flat hairline rows, Action Blue CTAs as pills, scale-0.95 press,
 * 44px targets. Token-backed colours only.
 */

import { Button, Sheet, SheetContent, SheetHeader, SheetTitle, Skeleton, StaleDataNotice, toast } from '@ai4nr/tricorder-ui'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, FileText, Sparkles, Plus, Trash2, PenLine } from 'lucide-react'

import {
  fetchProtocolVersions,
  fetchProtocolVersion,
  suggestDocuments,
  updateProtocolVersion,
  createProtocolVersion,
  submitProtocolVersion,
  archiveProtocolVersion,
  deleteProtocolVersion,
  deleteProtocol,
  ApiError,
  type ProtocolSummary,
  type ProtocolVersion,
  type SuggestedDocument,
} from '@/api/protocols'
import { useNavigate } from 'react-router-dom'
import { STATE_CHIP_CLASS } from './stateChip'
import { downloadJson } from '../../lib/download'
import {
  RELATED_ARTIFACT_TYPES,
  type ProtocolSpec,
  type RelatedArtifactType,
  type SpecRelatedArtifact,
} from '../../lib/studio/spec'

interface ProtocolDetailProps {
  protocolId: string | null
  protocolTitle: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ProtocolDetail({
  protocolId,
  protocolTitle,
  open,
  onOpenChange,
}: ProtocolDetailProps) {
  const { t } = useTranslation()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-md overflow-y-auto sm:max-w-md"
        data-testid="alg-protocol-detail"
      >
        <SheetHeader>
          <SheetTitle className="text-[19px] font-semibold tracking-[-0.01em] text-[var(--color-surface-ink)]">
            {protocolTitle || t('alg.manage.detailTitle', 'Protocol details')}
          </SheetTitle>
        </SheetHeader>

        {protocolId && (
          <div className="mt-6 space-y-8">
            <VersionsSection protocolId={protocolId} />
            <LinksSection protocolId={protocolId} />
            <ProtocolDangerZone
              protocolId={protocolId}
              onDeleted={() => onOpenChange(false)}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

// ── Versions ──────────────────────────────────────────────────────────────

function VersionsSection({ protocolId }: { protocolId: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['alg', 'protocol', protocolId, 'versions'],
    queryFn: () => fetchProtocolVersions(protocolId),
  })

  // Server-authoritative lifecycle mutations: call API, then invalidate.
  const lifecycle = useMutation({
    mutationFn: async (vars: { version: string; action: 'submit' | 'archive' }) => {
      if (vars.action === 'submit') {
        await submitProtocolVersion(protocolId, vars.version)
      } else {
        await archiveProtocolVersion(protocolId, vars.version)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alg', 'protocol', protocolId, 'versions'] })
      void queryClient.invalidateQueries({ queryKey: ['alg', 'protocols'] })
    },
    onError: (err) => {
      toast({ variant: 'destructive', description: failureText(err, t('alg.manage.lifecycleFailed', 'Action failed.')) })
    },
  })

  // The spec alone, which is what Import takes back.
  const download = useMutation({
    mutationFn: async (version: string) => {
      const read = await fetchProtocolVersion(protocolId, version)
      downloadJson(read.spec, `${protocolId}@${version}.json`)
    },
    onError: (err) => {
      toast({ variant: 'destructive', description: failureText(err, t('alg.manage.lifecycleFailed', 'Action failed.')) })
    },
  })

  // Server-authoritative per-version delete — available in ANY status (draft /
  // published / archived). Call API, then invalidate the version + catalog
  // lists so the row reflects committed state only.
  const remove = useMutation({
    mutationFn: (version: string) => deleteProtocolVersion(protocolId, version),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alg', 'protocol', protocolId, 'versions'] })
      void queryClient.invalidateQueries({ queryKey: ['alg', 'protocols'] })
    },
    onError: (err) => {
      toast({ variant: 'destructive', description: failureText(err, t('alg.manage.deleteVersionFailed', 'Could not delete version.')) })
    },
  })

  return (
    <section>
      <h3 className="mb-3 text-[14px] font-semibold uppercase tracking-wide text-[var(--color-surface-mute)]">
        {t('alg.manage.versions', 'Versions')}
      </h3>

      {isError && !data ? (
        <ErrorRow message={t('alg.manage.versionsLoadFailed', 'Could not load versions.')} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full rounded-[8px]" />
          <Skeleton className="h-16 w-full rounded-[8px]" />
        </div>
      ) : (data ?? []).length === 0 ? (
        <p className="text-[14px] text-[var(--color-surface-mute)]">
          {t('alg.manage.empty', 'No protocols yet')}
        </p>
      ) : (
        <ul className="space-y-2">
          {(data ?? []).map((v) => (
            <VersionRow
              key={v.version}
              version={v}
              busy={lifecycle.isPending || download.isPending || remove.isPending}
              onSubmit={() => lifecycle.mutate({ version: v.version, action: 'submit' })}
              onArchive={() => lifecycle.mutate({ version: v.version, action: 'archive' })}
              onDownload={() => download.mutate(v.version)}
              onDelete={() => remove.mutate(v.version)}
              onEdit={() => navigate(`/studio/${protocolId}/${v.version}`)}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

function VersionRow({
  version,
  busy,
  onSubmit,
  onArchive,
  onDownload,
  onDelete,
  onEdit,
}: {
  version: ProtocolSummary
  busy: boolean
  onSubmit: () => void
  onArchive: () => void
  onDownload: () => void
  onDelete: () => void
  onEdit: () => void
}) {
  const { t } = useTranslation()
  const statusLabel = t(`alg.manage.status.${version.status}`)

  return (
    <li className="rounded-[8px] border border-[var(--color-surface-border)] px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[17px] font-semibold text-[var(--color-surface-ink)]">
          {version.version}
        </span>
        <span className={`rounded-full border px-2 py-0.5 text-[12px] font-medium ${STATE_CHIP_CLASS[version.status]}`}>
          {statusLabel}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {/* Only entry point to Studio for a draft that isn't the catalog's
            newest-created row (e.g. an abandoned draft superseded by a later
            published version) — the Manage list row's own Edit button only
            renders for the single collapsed representative, so a non-newest
            draft was otherwise unreachable. Same navigation target as
            ManageRow's onEdit. */}
        {version.status === 'draft' && (
          <Button size="sm" variant="outline" disabled={busy} onClick={onEdit} data-testid="alg-edit-version">
            <PenLine className="h-4 w-4" />
            {t('alg.manage.editInStudio', 'Edit in Studio')}
          </Button>
        )}
        {version.status === 'draft' && (
          <Button size="sm" disabled={busy} onClick={onSubmit} data-testid="alg-submit">
            {t('alg.manage.submit', 'Submit')}
          </Button>
        )}
        {version.status === 'published' && (
          <Button size="sm" variant="outline" disabled={busy} onClick={onArchive} data-testid="alg-archive">
            {t('alg.manage.archive', 'Archive')}
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={busy} onClick={onDownload} data-testid="alg-download-spec">
          <Download className="h-4 w-4" />
          {t('alg.manage.downloadSpec', 'Download spec')}
        </Button>
        {/* Delete is available in ANY status (draft / published / archived) —
            every version is deletable by product decision. Server-authoritative
            (no confirm dialog). */}
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={onDelete}
          data-testid="alg-delete-version"
          className="text-[var(--color-tone-bad)] hover:text-[var(--color-tone-bad)]"
        >
          <Trash2 className="h-4 w-4" />
          {t('alg.manage.deleteVersion', 'Delete')}
        </Button>
      </div>
    </li>
  )
}

// ── Whole-protocol delete ───────────────────────────────────────────────────

// ProtocolDangerZone deletes the WHOLE protocol — every version of the id.
// Server-authoritative + no confirm dialog (explicit product decision): on
// success the catalog list is invalidated and the drawer closes; a refusal
// shows the server's reason.
function ProtocolDangerZone({
  protocolId,
  onDeleted,
}: {
  protocolId: string
  onDeleted: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const remove = useMutation({
    mutationFn: () => deleteProtocol(protocolId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alg', 'protocols'] })
      onDeleted()
    },
    onError: (err) => {
      toast({ variant: 'destructive', description: failureText(err, t('alg.manage.deleteProtocolFailed', 'Could not delete protocol.')) })
    },
  })

  return (
    <section>
      <Button
        variant="outline"
        disabled={remove.isPending}
        onClick={() => remove.mutate()}
        data-testid="alg-delete-protocol"
        className="w-full justify-center text-[var(--color-tone-bad)] hover:text-[var(--color-tone-bad)]"
      >
        <Trash2 className="h-4 w-4" />
        {t('alg.manage.deleteProtocol', 'Delete protocol')}
      </Button>
    </section>
  )
}

// ── Linked documents + suggestions ──────────────────────────────────────────

// A version's documents are part of its spec: the primary source is
// `source.sourceDocumentId`, the others are `relatedArtifacts`. Linking one is
// a spec edit, written like every other through the draft's PUT.
// A published or archived version is read-only: the author starts the next
// draft, which copies the spec and its documents, and edits that.

/** The version's spec: the Protocol message as protojson, which ProtocolSpec
 *  types. A write sends it back whole, its own change applied. */
function specOf(read: ProtocolVersion): ProtocolSpec {
  return read.spec as ProtocolSpec
}

/** The related artifacts the panel lists: those of a type it can name. */
function listedArtifacts(spec: ProtocolSpec | undefined): SpecRelatedArtifact[] {
  return (spec?.relatedArtifacts ?? []).filter((r) => RELATED_ARTIFACT_TYPES.includes(r.type))
}

const ARTIFACT_TYPE_KEY: Record<RelatedArtifactType, string> = {
  RELATED_ARTIFACT_TYPE_CITATION: 'citation',
  RELATED_ARTIFACT_TYPE_DOCUMENTATION: 'documentation',
  RELATED_ARTIFACT_TYPE_JUSTIFICATION: 'justification',
  RELATED_ARTIFACT_TYPE_DERIVED_FROM: 'derivedFrom',
}

function LinksSection({ protocolId }: { protocolId: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  // The newest version (the versions list is newest first) is the working
  // version; its read stays disabled until it is known.
  const { data: versionsData } = useQuery({
    queryKey: ['alg', 'protocol', protocolId, 'versions'],
    queryFn: () => fetchProtocolVersions(protocolId),
  })
  const version = versionsData?.[0]?.version
  const readKey = ['alg', 'protocol', protocolId, version, 'read'] as const

  // requireVersion narrows `version` to string inside the query closure
  // (instead of an `as string` cast). It never throws: the query is gated on
  // `version` via `enabled`.
  const requireVersion = (): string => {
    if (!version) throw new ApiError('NO_VERSION', 'protocol version not loaded', 400)
    return version
  }

  const {
    data: read,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: readKey,
    queryFn: () => fetchProtocolVersion(protocolId, requireVersion()),
    enabled: !!version,
  })

  const spec = read ? specOf(read) : undefined
  const primary = spec?.source?.sourceDocumentId || null
  const related = listedArtifacts(spec)
  const isDraft = read?.status === 'draft'

  // Flipped true after a 403 on a write: the caller may read this protocol
  // but not change it, so the write affordances go and a notice says why.
  const [accessDenied, setAccessDenied] = useState(false)

  const writeFailed = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 403) {
      setAccessDenied(true)
      toast({ variant: 'destructive', description: t('alg.manage.assignForbidden', 'You don\'t have edit access to this protocol.') })
    } else if (err instanceof ApiError && err.status === 409) {
      // No longer a draft: submitted or archived elsewhere. The read is
      // refreshed, so the write affordances follow the version's status.
      void queryClient.invalidateQueries({ queryKey: readKey })
      toast({ variant: 'destructive', description: failureText(err, fallback) })
    } else {
      toast({ variant: 'destructive', description: failureText(err, fallback) })
    }
  }

  // Suggestions are a document search built from the version itself (its
  // title, description, entry step and citation).
  const [suggestions, setSuggestions] = useState<SuggestedDocument[] | null>(null)
  const suggest = useMutation({
    mutationFn: (base: ProtocolVersion) => suggestDocuments(base),
    onSuccess: (docs) => setSuggestions(docs),
    onError: (err) => {
      toast({ variant: 'destructive', description: failureText(err, t('alg.manage.suggestFailed', 'Could not load suggestions.')) })
    },
  })

  // Server-authoritative: the PUT answers the version as stored, which
  // replaces the cached read.
  const saved = (next: ProtocolVersion) => {
    queryClient.setQueryData(readKey, next)
    void queryClient.invalidateQueries({ queryKey: ['alg', 'protocol', protocolId, 'versions'] })
  }

  const cite = useMutation({
    mutationFn: ({ base, doc }: { base: ProtocolVersion; doc: SuggestedDocument }) => {
      const current = specOf(base)
      const entry: SpecRelatedArtifact = {
        type: 'RELATED_ARTIFACT_TYPE_CITATION',
        documentId: doc.document_id,
        display: doc.title,
      }
      return updateProtocolVersion(base, {
        ...current,
        relatedArtifacts: [...(current.relatedArtifacts ?? []), entry],
      })
    },
    onSuccess: (next, { doc }) => {
      setSuggestions((prev) => prev?.filter((s) => s.document_id !== doc.document_id) ?? null)
      saved(next)
    },
    onError: (err) => writeFailed(err, t('alg.manage.assignFailed', 'Could not link the document.')),
  })

  const uncite = useMutation({
    mutationFn: ({ base, documentId }: { base: ProtocolVersion; documentId: string }) => {
      const current = specOf(base)
      const kept = (current.relatedArtifacts ?? []).filter((r) => r.documentId !== documentId)
      return updateProtocolVersion(base, { ...current, relatedArtifacts: kept })
    },
    onSuccess: saved,
    onError: (err) => writeFailed(err, t('alg.manage.unlinkFailed', 'Could not remove the document.')),
  })

  // The next draft continues this version: its spec (documents included),
  // title, description, institution, skills and privacy.
  const nextDraft = useMutation({
    mutationFn: (base: ProtocolVersion) => createProtocolVersion(base, base.spec),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alg', 'protocol', protocolId, 'versions'] })
      void queryClient.invalidateQueries({ queryKey: ['alg', 'protocols'] })
    },
    onError: (err) => writeFailed(err, t('alg.manage.nextDraftFailed', 'Could not start the next draft.')),
  })

  const writing = cite.isPending || uncite.isPending || nextDraft.isPending
  const linked = new Set([...(primary ? [primary] : []), ...related.map((r) => r.documentId)])

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-[14px] font-semibold uppercase tracking-wide text-[var(--color-surface-mute)]">
          {t('alg.manage.links', 'Linked documents')}
        </h3>
        {!accessDenied && isDraft && read && (
          <Button
            size="sm"
            variant="ghost"
            disabled={suggest.isPending}
            onClick={() => suggest.mutate(read)}
            data-testid="alg-suggest-documents"
          >
            <Sparkles className="h-4 w-4" />
            {suggest.isPending
              ? t('alg.manage.suggesting', 'Loading suggestions …')
              : t('alg.manage.suggest', 'Suggest documents')}
          </Button>
        )}
      </div>

      {accessDenied && (
        <p
          role="status"
          className="mb-3 text-[13px] text-[var(--color-surface-mute)]"
          data-testid="alg-links-readonly"
        >
          {t('alg.manage.readOnly', 'View only — you don\'t have edit access to this protocol.')}
        </p>
      )}

      {!accessDenied && read && !isDraft && (
        <div className="mb-3 flex items-center justify-between gap-3" data-testid="alg-links-version-readonly">
          <p className="text-[13px] text-[var(--color-surface-mute)]">
            {t(
              'alg.manage.versionReadOnly',
              'This version is no longer a draft, so its documents are read-only. Start the next draft to change them.',
            )}
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={writing}
            onClick={() => nextDraft.mutate(read)}
            data-testid="alg-next-draft"
          >
            <PenLine className="h-4 w-4" />
            {t('alg.manage.nextDraft', 'Start next draft')}
          </Button>
        </div>
      )}

      {read && <StaleDataNotice isFetching={isFetching} isError={isError} className="mb-2" />}
      {isError && !read ? (
        <ErrorRow message={t('alg.manage.linksLoadFailed', 'Could not load linked documents.')} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <Skeleton className="h-12 w-full rounded-[8px]" />
      ) : !primary && related.length === 0 ? (
        <p className="text-[14px] text-[var(--color-surface-mute)]">
          {t('alg.manage.linksEmpty', 'No linked documents')}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="alg-links-list">
          {primary && (
            <DocumentRow label={primary} kind={t('alg.manage.primarySource', 'Primary source')} />
          )}
          {related.map((r) => (
            <DocumentRow
              key={r.documentId}
              label={r.display || r.documentId}
              kind={t(`alg.manage.artifactType.${ARTIFACT_TYPE_KEY[r.type]}`)}
              onRemove={
                isDraft && !accessDenied && read
                  ? () => uncite.mutate({ base: read, documentId: r.documentId })
                  : undefined
              }
              removing={writing}
            />
          ))}
        </ul>
      )}

      {/* Matching documents */}
      {suggestions !== null && isDraft && (
        <div className="mt-4">
          {suggestions.length === 0 ? (
            <p className="text-[14px] text-[var(--color-surface-mute)]">
              {t('alg.manage.suggestEmpty', 'No matching documents found')}
            </p>
          ) : (
            <ul className="space-y-2" data-testid="alg-suggestions-list">
              {suggestions.map((s) => (
                <li
                  key={s.document_id}
                  className="flex items-center gap-3 rounded-[8px] border border-dashed border-[var(--color-surface-border)] px-4 py-3"
                >
                  <FileText className="h-4 w-4 shrink-0 text-[var(--color-surface-mute)]" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] text-[var(--color-surface-ink)]">{s.title}</p>
                    <p className="text-[12px] text-[var(--color-surface-mute)]">
                      {t('alg.manage.score', 'Match {{score}}', { score: s.score.toFixed(2) })}
                    </p>
                  </div>
                  {!accessDenied && read && !linked.has(s.document_id) && (
                    <Button
                      size="sm"
                      disabled={writing}
                      onClick={() => cite.mutate({ base: read, doc: s })}
                      data-testid="alg-assign-document"
                    >
                      <Plus className="h-4 w-4" />
                      {t('alg.manage.assign', 'Link')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

function DocumentRow({
  label,
  kind,
  onRemove,
  removing = false,
}: {
  label: string
  kind: string
  onRemove?: () => void
  removing?: boolean
}) {
  const { t } = useTranslation()
  return (
    <li className="flex items-center gap-3 rounded-[8px] border border-[var(--color-surface-border)] px-4 py-3">
      <FileText className="h-4 w-4 shrink-0 text-[var(--color-surface-mute)]" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] text-[var(--color-surface-ink)]">{label}</p>
        <p className="text-[12px] text-[var(--color-surface-mute)]">{kind}</p>
      </div>
      {onRemove && (
        <Button
          size="sm"
          variant="ghost"
          disabled={removing}
          onClick={onRemove}
          aria-label={t('alg.manage.unlink', 'Remove')}
          data-testid="alg-unlink-document"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </li>
  )
}

// ── Shared error text and row ─────────────────────────────────────────────────

/** The server's own reason when it gave one, else the surface's fallback. */
function failureText(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.status > 0 ? err.message : fallback
}


function ErrorRow({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 rounded-[8px] border border-[var(--color-tone-bad)]/30 px-4 py-3 text-[14px] text-[var(--color-tone-bad)]"
    >
      <span>{message}</span>
      <Button size="sm" variant="outline" onClick={onRetry}>
        {t('alg.run.retry', 'Try again')}
      </Button>
    </div>
  )
}
